import type { AdminGiftListResponse } from "@/types/admin-gift";

/**
 * Fixture for GET /admin/users/{userId}/gifts (mock mode). Global — one
 * fixture answers every user, mirroring `MOCK_LOST_REPORTS`'s simplicity
 * (there is no per-user gift scenario table yet).
 *
 * Deliberately a THREE-gift stacked chain, so local dev / mock-mode test
 * runs make both queue-note states visible (I4's whole point — mock mode
 * previously answered "no gifts" for every user, which is indistinguishable
 * from the truth on the exact screen built to prevent that confusion):
 *   - id 1 (ended) -> id 2 (active): the queue note must be ABSENT above the
 *     active gift. This is the C1 regression made concrete — a false
 *     "queued next" line above a gift that has already started.
 *   - id 2 (active) -> id 3 (scheduled): the queue note MUST be present
 *     above the scheduled gift — it genuinely hasn't started, and the
 *     active gift is still occupying the queue ahead of it.
 *
 * Plus id 4 (WS-4 slice 4.5): a gift waiting on hardware delivery — the state
 * a gift bought for a NON-member sits in between redemption and the courier
 * arriving. That is the most common pending gift in production (buying Zone for
 * someone who doesn't have it is the main way a gift is bought) and it is
 * dateless by design, so it must be visible locally too.
 */
export const MOCK_GIFTS: AdminGiftListResponse = {
  items: [
    {
      id: 1,
      orderNumber: "gift_mock0001",
      status: "ended",
      startsAt: "2024-07-01T02:00:00Z",
      endsAt: "2025-07-01T02:00:00Z",
      redeemedAt: "2024-06-20T14:00:00Z",
      activatedAt: "2024-07-01T02:00:00Z",
      pendingReason: null,
      history: [
        {
          at: "2024-06-20T14:00:00Z",
          fromStatus: null,
          toStatus: "scheduled",
          reason: "gift.redeemed",
        },
        {
          at: "2024-07-01T02:00:00Z",
          fromStatus: "scheduled",
          toStatus: "active",
          reason: "gift.started",
        },
        {
          at: "2025-07-01T02:00:00Z",
          fromStatus: "active",
          toStatus: "ended",
          reason: "gift.term_ended",
        },
      ],
    },
    {
      id: 2,
      orderNumber: "gift_mock0002",
      status: "active",
      startsAt: "2025-07-01T02:00:00Z",
      endsAt: "2026-07-01T02:00:00Z",
      redeemedAt: "2024-06-20T14:05:00Z",
      activatedAt: "2025-07-01T02:00:00Z",
      pendingReason: null,
      history: [
        {
          at: "2024-06-20T14:05:00Z",
          fromStatus: null,
          toStatus: "scheduled",
          reason: "gift.redeemed",
        },
        {
          at: "2025-07-01T02:00:00Z",
          fromStatus: "scheduled",
          toStatus: "active",
          reason: "gift.started",
        },
      ],
    },
    {
      id: 3,
      orderNumber: "gift_mock0003",
      status: "scheduled",
      startsAt: "2026-07-01T02:00:00Z",
      endsAt: "2027-07-01T02:00:00Z",
      redeemedAt: "2024-06-20T14:10:00Z",
      activatedAt: null,
      pendingReason: null,
      history: [
        {
          at: "2024-06-20T14:10:00Z",
          fromStatus: null,
          toStatus: "scheduled",
          reason: "gift.redeemed",
        },
      ],
    },
    {
      id: 4,
      orderNumber: "gift_mock0004",
      status: "pending",
      // No dates: the twelve months start the day the hardware arrives.
      startsAt: null,
      endsAt: null,
      redeemedAt: "2026-07-28T09:30:00Z",
      activatedAt: null,
      pendingReason: "awaiting_delivery",
      history: [
        {
          at: "2026-07-28T09:30:00Z",
          fromStatus: null,
          toStatus: "pending",
          reason: "gift.redeemed",
        },
      ],
    },
  ],
};
